import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { AppDispatch } from 'containers/Femtasy';

import {
  ActiveSubscriptionFragment,
  UserSubscription,
} from 'graphql/generated/graphql';

import analytics from 'helpers/analytics';
import { isInTrial } from 'helpers/subscription';

import api from 'api';
import { IUser } from 'types/iuser';

import { authSuccess, enableRememberMe } from './auth';
import { resetPlayerThunk } from './player';
import { getUserProfile } from './profile';
import { fetchUserAlreadyListenedStories } from './userAlreadyListenedStories';

interface ApiError {
  message: string;
  status?: number;
  code?: string;
  response?: {
    data?: {
      errors?: string[];
    };
  };
}

export interface SignInState {
  isLoading: boolean;
  user: IUser | Object;
  error: ApiError | null;
}

export const initialSignInState: SignInState = {
  isLoading: false,
  user: {},
  error: null,
};

const signInSlice = createSlice({
  name: 'signIn',
  initialState: initialSignInState,
  reducers: {
    signInLoading: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    signInSuccess: {
      reducer: (state, action: PayloadAction<IUser | Object>) => {
        state.user = action.payload;
        state.isLoading = false;
      },
      prepare: (response: any, rememberMe?: boolean) => {
        const user = response.data.data || response.data.user;

        if (rememberMe) {
          analytics.track('login-with-remember-me', { category: 'user' });
        }

        return { payload: user };
      },
    },
    signInError: {
      reducer: (state, action: PayloadAction<ApiError>) => {
        state.isLoading = false;
        state.error = action.payload;
      },
      prepare: (error?: unknown) => {
        const axiosError = error as AxiosError<{ errors?: string[] }>;

        return {
          payload: {
            message: axiosError?.message,
            status: axiosError?.response?.status,
            code: axiosError?.code,
            response: {
              data: {
                errors: axiosError?.response?.data?.errors,
              },
            },
          },
        };
      },
    },
  },
});

export const { signInLoading, signInSuccess, signInError } =
  signInSlice.actions;

export default signInSlice.reducer;

export const afterSuccessSignIn = async (dispatch, response, rememberMe?) => {
  dispatch(resetPlayerThunk());

  dispatch(authSuccess());
  dispatch(signInSuccess(response, rememberMe));
  dispatch(fetchUserAlreadyListenedStories());

  const {
    id,
    trackingId,
    activeSubscription,
  }: { id: string; trackingId: string; activeSubscription: UserSubscription } =
    await dispatch(getUserProfile());

  analytics.identify({
    userId: id,
    userIdHash: trackingId,
    ...parseSubscriptionTrackingData(activeSubscription),
  });
};

export const signIn =
  ({ email, password, rememberMe }) =>
  async (dispatch: AppDispatch) => {
    dispatch(signInLoading());

    try {
      if (rememberMe) {
        dispatch(enableRememberMe());
      }

      const res = await api.auth.signIn(email.toLowerCase(), password);

      return afterSuccessSignIn(dispatch, res, rememberMe);
    } catch (err) {
      dispatch(signInError(err as AxiosError));
    }
  };

const parseSubscriptionTrackingData = (
  activeSubscription: ActiveSubscriptionFragment | null,
) => {
  const isTrialer = isInTrial(activeSubscription?.freeTrialEndsAt);
  const subscriptionStatus =
    activeSubscription?.billingPeriod.unit ?? 'no-subscription';

  return {
    isTrialer: isTrialer ? 'yes' : 'no',
    subscriptionStatus,
  };
};
